/**
 * Telemetry disposition for a failed Axios response. Does not change
 * promise rejection — Axios always rejects; this only controls reporting.
 *
 * - `'ignore'`: emit no warning/error telemetry; still reject
 * - `'warning'`: emit warning-level telemetry; still reject
 * - `'error'`: emit error-level telemetry; still reject
 */
export type ApiErrorDisposition = 'ignore' | 'warning' | 'error'

/**
 * Status-only error shape for classifiers. Do not type this as `AxiosError`:
 * the host app and `@labrav/common` resolve different axios copies, so those
 * `AxiosError` identities are not assignable to each other.
 */
export type ApiErrorLike = {
  response?: { status?: number }
}

/**
 * Per-request classifier. Return a disposition to override the default
 * policy, or `undefined` to fall back to `defaultErrorDisposition`.
 */
export type ClassifyApiError = (
  error: ApiErrorLike
) => ApiErrorDisposition | undefined

export function defaultErrorDisposition(
  error: ApiErrorLike
): ApiErrorDisposition {
  const status = error.response?.status
  if (status !== undefined && status >= 400 && status < 500) {
    return 'warning'
  }
  return 'error'
}

/**
 * Classifier for known empty-state endpoints where HTTP 404 means the
 * resource is absent by design. Returns `undefined` for other statuses.
 */
export const ignoreNotFound: ClassifyApiError = error =>
  error.response?.status === 404 ? 'ignore' : undefined
